/**
 * Character limits for string fields, shared between the create and edit
 * flows so the two validation paths cannot drift apart (a string created
 * within these limits must remain valid when reopened for editing).
 */
export const SOURCE_TEXT_CHAR_LIMIT = 2000;
export const CONTEXT_NOTE_CHAR_LIMIT = 5000;

/**
 * Upper bound on pages fetched when walking a version snapshot's paginated
 * strings. Shared by every snapshot reader so a backend that never returns a
 * null nextToken cannot hang the UI in an unbounded loop.
 */
export const MAX_SNAPSHOT_PAGES = 100;

/**
 * Page size requested when walking a version snapshot's strings. Pinned (rather
 * than left to the backend default) so MAX_SNAPSHOT_PAGES bounds the same
 * number of strings for every reader.
 */
export const SNAPSHOT_PAGE_SIZE = 100;

/**
 * Maximum strings a single group may hold. Shared by the create form's
 * validation and the strings table header so the advertised limit and the
 * enforced limit cannot drift apart.
 */
export const MAX_STRINGS_PER_GROUP = 25;

/**
 * Maximum length of a string ID. Capped at 50 by product requirement —
 * deliberately stricter than the backend `StringIdentifier` Smithy constraint
 * (`@length(min: 1, max: 200)`) so a namespace name, string group name, and
 * string ID all share the same 50-character ceiling.
 */
export const STRING_ID_MAX_LENGTH = 50;

/**
 * Regex for valid string IDs — must contain only letters, digits, hyphens, or
 * underscores and include at least one alphanumeric character. Matches the
 * backend Smithy constraint: `(?=.*[a-zA-Z0-9])[a-zA-Z0-9_-]+`
 */
export const STRING_ID_PATTERN = /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9_-]+$/;

/**
 * Resource id of the error shown when a string ID fails the pattern check.
 */
export const STRING_ID_FORMAT_ERROR_KEY = 'sgd-string-id-format';

/**
 * Resource id of the error shown when a string ID exceeds the maximum length.
 * Takes {@link STRING_ID_MAX_LENGTH} as its `max` ICU parameter.
 */
export const STRING_ID_MAX_LENGTH_ERROR_KEY = 'sgd-string-id-too-long';

/**
 * Resource ids of the flashbar copy for the transient "string group not ready"
 * state (backend 409 mapped to StringGroupNotReadyException): a fresh submission
 * published successfully but TIS has not finished ingesting the new version yet,
 * so it is not queryable.
 */
export const STRING_GROUP_NOT_READY_HEADER_KEY = 'sgd-not-ready-header';
export const STRING_GROUP_NOT_READY_MESSAGE_KEY = 'sgd-not-ready-body';

/**
 * Interim-period copy for the window between submitting a string group for
 * translation and TRANSCOM confirming the submission (~3 minutes).
 *
 * The pending state is server-derived: while the submission is unconfirmed,
 * describeStringGroup answers with StringGroupNotReadyException ("no metadata
 * found yet"). TRANSCOM has confirmed the submission the moment that call
 * stops erroring and returns the version. Because the signal lives on the
 * server, the pending screen survives a reload, a new tab and a different
 * device for free — see StringGroupDetailPage for the wiring.
 */

/**
 * Hover reason shown on the Rollback button when a translation is in progress.
 * Rollback is functionally a new submission, so it is blocked the same way
 * Submit is while a translation job is active.
 */
export const ROLLBACK_IN_PROGRESS_LOCK_REASON_KEY =
  'sgd-lock-rollback-in-progress';

/** Hover reason shown on every control locked while a submission is pending. */
export const SUBMISSION_PENDING_LOCK_REASON_KEY = 'sgd-lock-submission-pending';

/**
 * Hover reason shown on Cancel after a cancellation has been requested but the
 * group is still reported IN_PROGRESS. TRANSCOM processes the cancellation
 * asynchronously (a CancellationEvent flips the group to CANCELLED only once the
 * content-sync workflow handles it), so the status lingers on IN_PROGRESS for a
 * while after the mutation returns. Keeping Cancel locked for that window stops
 * the user re-issuing a cancellation that is already in flight.
 */
export const CANCELLATION_PENDING_LOCK_REASON_KEY =
  'sgd-lock-cancellation-pending';

/**
 * Hover reason shown on Submit when the group has no strings. A group is only
 * created in TRANSCOM on its first submission, and TRANSCOM rejects a
 * submission that carries no strings — so Submit stays disabled until the group
 * holds at least one string to send.
 */
export const SUBMIT_NO_STRINGS_LOCK_REASON_KEY = 'sgd-lock-submit-no-strings';

/**
 * Loss warning shown on a brand-new group that has never been submitted. Such a
 * group exists only in the browser (the create form's data plus any drafted
 * strings) — it is not created in TRANSCOM until its first submission. Leaving
 * the page beforehand discards it, so this note tells the user to submit to keep
 * it.
 */
export const UNSUBMITTED_GROUP_LOSS_HEADER_KEY = 'sgd-unsubmitted-loss-header';
export const UNSUBMITTED_GROUP_LOSS_MESSAGE_KEY = 'sgd-unsubmitted-loss-body';

/**
 * Hover reason shown on Edit Group when the draft already holds string-level
 * changes. Saving a group edit chains straight into the submit dialog, and
 * cancelling that dialog discards the whole draft (see StringGroupDetailPage.
 * handleEditGroupSave / SubmitTranslationModal). Blocking the entry point while
 * string additions/edits/deletions are staged keeps that cancel from silently
 * dropping them — the user submits or discards those changes first.
 */
export const EDIT_GROUP_PENDING_CHANGES_LOCK_REASON_KEY =
  'sgd-lock-edit-group-pending-changes';

/**
 * Edit Group before the group has ever been submitted for translation (M1).
 * Editing a group chains straight into the submit-for-translation dialog, so it
 * only makes sense once the group has a first submission to build on. (M2 will
 * handle the draft-before-first-submit case differently.)
 */
export const EDIT_GROUP_NOT_SUBMITTED_LOCK_REASON_KEY =
  'sgd-lock-edit-group-not-submitted';

/**
 * Edit Group while a translation job is in progress. Editing chains into a new
 * submission, which would start a second translation (V2) on top of the
 * in-flight one (V1) and mark V1 as done prematurely.
 */
export const EDIT_GROUP_IN_PROGRESS_LOCK_REASON_KEY =
  'sgd-lock-edit-group-in-progress';

/** Note restating that edits to the whole group are paused while pending. */
export const SUBMISSION_PENDING_NOTE_KEY = 'sgd-submission-pending-note';

/** Heading for the interim pending banner. */
export const SUBMISSION_PENDING_BANNER_HEADER_KEY =
  'sgd-submission-pending-header';

/**
 * Body for the interim pending banner. Indeterminate by design (no percentage,
 * no stages): the work happens server-side, so the copy states the typical
 * wait, reassures the user it is safe to leave, and says how they will be told.
 *
 * One resource per recipient shape rather than a `recipient` that can hold
 * "you" — see `src/i18n/inlineSlot.ts` for why a fragment cannot be translated.
 */
export const SUBMISSION_PENDING_BANNER_BODY_KEY = 'sgd-submission-pending-body';
export const SUBMISSION_PENDING_BANNER_BODY_NO_ADDRESS_KEY =
  'sgd-submission-pending-body-you';

/** Heading for the post-submit acknowledgement modal ("safe to leave"). */
export const SUBMISSION_ACK_HEADER_KEY = 'sgd-submission-ack-header';
